import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBack, Close, Search } from '@mui/icons-material';
import { searchCandidateProfileQueryHistoryListSx } from 'modules/candidate-profile/styles';
import { useSearchCandidateProfileQueryHistoryList } from 'modules/candidate-profile/hooks/useSearchCandidateProfileQueryHistoryList';
import { useSearchCandidateProfileQueryConversationHistoryActions } from 'modules/candidate-profile/hooks/useSearchCandidateProfileQueryConversationHistoryActions';
import { SearchCandidateProfileQueryHistoryListItems } from './SearchCandidateProfileQueryHistoryListItems';
import { EmptyState } from 'system';
import {
  HandleSelectSearchCandidateProfileQueryProps,
  useSelectSearchCandidateProfileQuery
} from 'modules/candidate-profile/hooks/useSelectSearchCandidateProfileQuery';

type SearchCandidateProfileQueryHistoryListProps = {
  onSelectSearchCandidateProfileQuery: (
    data: HandleSelectSearchCandidateProfileQueryProps
  ) => void;
  onClose: () => void;
  onDeleteSearchCandidateProfileQuery: (id: string) => void;
};

export const SearchCandidateProfileQueryHistoryList: React.FC<
  SearchCandidateProfileQueryHistoryListProps
> = ({
  onSelectSearchCandidateProfileQuery,
  onClose,
  onDeleteSearchCandidateProfileQuery
}) => {
  const {
    pinnedList,
    recentList,
    loading,
    searchTerm,
    setSearchTerm,
    updatePinStatus,
    updateDisplayTitle,
    removeItem
  } = useSearchCandidateProfileQueryHistoryList();
  const { getMenuOptions } =
    useSearchCandidateProfileQueryConversationHistoryActions({
      updatePinStatus,
      updateDisplayTitle,
      removeItem,
      onDelete: onDeleteSearchCandidateProfileQuery
    });
  const { handleItemClick } = useSelectSearchCandidateProfileQuery({
    onSelectSearchCandidateProfileQuery,
    onSelectCurrent: onClose
  });

  const hasPinned = pinnedList.length > 0;
  const hasRecent = recentList.length > 0;
  const isEmpty = !hasPinned && !hasRecent;

  return (
    <Box sx={searchCandidateProfileQueryHistoryListSx.container}>
      <Box sx={searchCandidateProfileQueryHistoryListSx.fixedHeader}>
        <Box sx={searchCandidateProfileQueryHistoryListSx.topRow}>
          <Box
            sx={searchCandidateProfileQueryHistoryListSx.backButton}
            onClick={onClose}
          >
            <ArrowBack sx={{ fontSize: 20 }} />
            <Typography
              sx={searchCandidateProfileQueryHistoryListSx.backButtonText}
            >
              Back to search
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" color="primary">
            <Close />
          </IconButton>
        </Box>

        <Typography sx={searchCandidateProfileQueryHistoryListSx.title}>
          Search history
        </Typography>

        <TextField
          fullWidth
          placeholder="Search your history"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={searchCandidateProfileQueryHistoryListSx.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {loading && (
        <Box sx={searchCandidateProfileQueryHistoryListSx.loadingContainer}>
          <CircularProgress />
        </Box>
      )}

      {!loading && isEmpty && (
        <EmptyState
          type="no-results"
          title="Nothing found"
          subtitle="Search for something else"
        />
      )}

      {!loading && !isEmpty && (
        <Box sx={searchCandidateProfileQueryHistoryListSx.listContainer}>
          {hasPinned && (
            <Box sx={searchCandidateProfileQueryHistoryListSx.section}>
              <Typography
                sx={searchCandidateProfileQueryHistoryListSx.sectionHeader}
              >
                Pinned
              </Typography>
              <SearchCandidateProfileQueryHistoryListItems
                items={pinnedList}
                getMenuOptions={getMenuOptions}
                onItemClick={handleItemClick}
              />
            </Box>
          )}

          {hasRecent && (
            <Box sx={searchCandidateProfileQueryHistoryListSx.section}>
              <Typography
                sx={searchCandidateProfileQueryHistoryListSx.sectionHeader}
              >
                Recent
              </Typography>
              <SearchCandidateProfileQueryHistoryListItems
                items={recentList}
                getMenuOptions={getMenuOptions}
                onItemClick={handleItemClick}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
